import { createContext, useContext, type Provider } from 'react';

import {
	type ConversationAssistantConfig,
	type OnHandOffPayload,
	type ProfileData,
} from '../../../types';
import { CSM_AI_EXPERIENCE_ID } from '../../constants/private';
import type { AuthType } from '../auth-type-from-props';

export type GlobalConfig = {
	profileData: ProfileData;
	/**
	 * The authentication type for the current user session.
	 * - `'oauth'`: User authenticated via OAuth.
	 * - `'anonymous'`: User is not logged in (anonymous access).
	 * - `'session'`: User authenticated via session cookie.
	 */
	authType?: AuthType;
	closeChat: () => void;
	onError: (error: Error) => void;
	onHandoff?: (payload: OnHandOffPayload) => void;
	/**
	 * Callback invoked when the unread messages state changes.
	 * `true` means there are unread messages, `false` means all messages have been read.
	 */
	onHasUnreadMessagesChanged?: (hasUnreadMessages: boolean) => void;
	/**
	 * Gateway origin (scheme + host) shared by backend services (e.g. assistance service, OCG).
	 * When unset, clients use relative paths (same-origin).
	 */
	gatewayOrigin?: string;
} & Pick<ConversationAssistantConfig, 'siteId' | 'orgId'> &
	Required<Pick<ConversationAssistantConfig, 'experienceId' | 'widgetId'>>;

const globalConfig = createContext<GlobalConfig | null>(null);

export const GlobalConfigProvider: Provider<GlobalConfig | null> = globalConfig.Provider;

export const useGlobalConfig = (): GlobalConfig => {
	const context = useContext(globalConfig);

	if (!context) {
		throw new Error('`useGlobalConfig` must be rendered with `GlobalConfigProvider`');
	}

	return context;
};

export type CreateGlobalConfigArgs = ConversationAssistantConfig & {
	profileData: ProfileData;
	authType?: AuthType;
	closeChat: () => void;
	onError: (error: Error) => void;
	onHandoff?: (payload: OnHandOffPayload) => void;
	onHasUnreadMessagesChanged?: (hasUnreadMessages: boolean) => void;
};

export const createGlobalConfig = ({
	profileData,
	authType,
	widgetId,
	siteId,
	experienceId = CSM_AI_EXPERIENCE_ID,
	orgId,
	closeChat,
	onError,
	onHandoff,
	onHasUnreadMessagesChanged,
	gatewayOrigin,
}: CreateGlobalConfigArgs): GlobalConfig => ({
	profileData,
	authType,
	widgetId: widgetId ?? null,
	siteId,
	experienceId,
	orgId,
	closeChat,
	onError,
	onHandoff,
	onHasUnreadMessagesChanged,
	gatewayOrigin,
});
